import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // SRAM: icon rules select on :is(svg, [data-slot='icon']) rather than svg alone.
  // The Icon component renders <span class="material-symbols-sharp">, which the
  // upstream svg-scoped rules silently miss — no error, just a 24px glyph beside
  // lucide's 16px and padding that never reduces (CRCB2B-643).
  // SRAM: 3% letter-spacing throughout (Figma reports letterSpacing: 3 unitless
  // file-wide and the design owner confirmed percent). Label size is per-size,
  // not a base class, so it no longer pins every button to 14px (CRCB2B-643).
  // SRAM: disabled is ONE fill for every variant (--disabled #bcbcbc + a
  // --disabled-foreground label), read off all nine Figma variants at both sizes.
  // Upstream's disabled:opacity-50 is deliberately gone: fading each variant's own
  // fill yields nine different disabled colors, where the design collapses them
  // onto one. `white` disabled goes grey too — it does not stay white (CRCB2B-643).
  //
  // GHOST DISABLED — a deliberate divergence from the Icon Button node, decided by
  // the project owner, not an oversight. The two Figma components disagree: the TEXT
  // button's Ghost disabled DOES declare the shared #bcbcbc fill (1573:22078), while
  // the ICON button's Ghost disabled declares NO fill and greys the glyph only
  // (1658:21756, confirmed by screenshot). Both were read directly. We follow the
  // TEXT button for both, because the icon button's unfilled disabled does not
  // differentiate the state strongly enough to read as disabled. Every ghost
  // treatment — neutral and chromatic, text and icon-only — therefore takes the
  // shared fill. Raised on the 6.9b design list so the designer knows the two
  // components disagree. `link` is the one opt-out, for an unrelated reason
  // (see the compoundVariant below).
  //
  // SRAM: text/icon-only both support icon BEFORE or AFTER the label by DOM order
  // alone — the base is a flex row with a gap and symmetric padding, so no
  // position-aware prop or logic exists or is needed (CRCB2B-643 task 2.4).
  //
  // SRAM: icon SIZE is per-size, not a base class. Figma's text Button uses
  // icon/lg 24px at Default (107:765) and icon/default 20px at Small (367:2611);
  // the Icon Button uses 24px at EVERY size, including its 32px Small (1658:21776).
  // Upstream pinned every icon to size-4 (16px), which matches no Figma step.
  //
  // Each size carries TWO icon rules, because the two icon kinds are scaled by
  // different CSS properties (task 2.4a):
  //   size-*     -> width/height, which is how a lucide <svg> scales.
  //   text-[Npx] -> font-size, the ONLY thing that scales a Material Symbols
  //                 glyph. It is a FONT, so constraining its box does nothing;
  //                 the box-only rule was the 2.4a bug (24px glyph in a 20px box).
  // The font-size rule is a descendant selector, so it outranks the 24px default
  // on .material-symbols-sharp itself. It deliberately has NO :not() escape: an
  // icon that wants out passes Icon's `size` prop, which lands inline and beats
  // every class. The size-* rules keep their escape hatch for svg call sites.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-ui font-medium tracking-[0.03em] transition-all disabled:pointer-events-none disabled:bg-disabled disabled:text-disabled-foreground disabled:border-border [&_:is(svg,[data-slot=icon])]:pointer-events-none shrink-0 [&_:is(svg,[data-slot=icon])]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      // Status fills carry WHITE labels, not their tinted --*-foreground tokens:
      // Figma draws them white, and white scores higher on every one of them.
      //
      // ACTIVE reuses the HOVER treatment throughout (permitted by the spec; Figma
      // draws no pressed state). It is stated explicitly rather than left to :hover
      // because :hover is unreliable on touch and never fires for a keyboard
      // Space/Enter activation, where :active does (CRCB2B-643 task 2.2).
      //
      // THE ICON BUTTON'S `Style` AXIS (Filled/Subtle/Ghost, node 128:1827) IS
      // EXPRESSED ON THIS VARIANT AXIS, NOT AS A THIRD CVA AXIS. Reading the matrix,
      // each Style already corresponds to a variant we have or a single new name:
      //   Filled → the solid fills (default/brand/success/destructive/warning/white)
      //   Subtle → `outline`, which it matches exactly: 1px --border-color-strong
      //            #676767 resting, --border-color-default-hover #bcbcbc on hover,
      //            no fill, shared disabled fill (1658:21658 / :21676 / :21696)
      //   Ghost  → `ghost` for Neutral; `*-ghost` below for the chromatic ones
      // A third axis would advertise 12 × 3 pairs where Figma defines 9, and would
      // need compoundVariants to suppress the rest. Encoding Style in the variant
      // NAME makes only the defined combinations expressible: Figma gives Neutral all
      // three styles, Success/Attention/Warning Filled + Ghost, and Brand + White
      // Filled only — so there is deliberately no brand-ghost, white-ghost,
      // info-ghost, or *-subtle name. Follows the existing `white-outline`
      // color-then-style naming precedent (CRCB2B-643 task 2.1i).
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-hover",
        destructive:
          "bg-destructive text-white hover:bg-destructive-hover active:bg-destructive-hover focus-visible:ring-destructive/20",
        // Outline rests on --border-strong #676767, NOT the field border --border
        // #d6d6d6, and hovers by lightening the boundary to --border-hover #bcbcbc
        // with the fill left alone. Upstream's hover:bg-accent fill swap is dropped:
        // Figma's Outline hover changes the border only (nodes 107:772 / 539:2781).
        // This is also the icon button's Subtle style — see the note above.
        outline:
          "border border-border-strong bg-background text-foreground shadow-ui hover:border-border-hover active:border-border-hover",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary-hover active:bg-secondary-hover",
        // Ghost hovers to fill-neutral-subtle-hover #d6d6d6 (--accent-hover), one
        // step darker than upstream's --accent #eaeaea (node 1573:22120). The icon
        // button's Neutral Ghost hover instead keeps no fill and darkens the glyph to
        // #585858 (1658:21736); one hover treatment is used for both, for the same
        // state-legibility reason as the disabled ruling above. On the 6.9b list.
        ghost:
          "text-foreground hover:bg-accent-hover hover:text-accent-foreground active:bg-accent-hover active:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline active:underline",
        brand: "bg-brand text-brand-foreground hover:bg-brand-hover active:bg-brand-hover",
        success: "bg-success text-white hover:bg-success-hover active:bg-success-hover",
        // KNOWN WCAG AA FAILURE, built as designed: --warning #b06b00 measures
        // 4.24:1 against white (4.5 required). Raised with design — yellow-600
        // #935900 (5.72:1) and yellow-700 #7e4b00 (7.26:1) are already in the
        // ramp if they want a compliant step. Not substituted unilaterally.
        warning: "bg-warning text-white hover:bg-warning-hover active:bg-warning-hover",
        // --info rides the INTERIM blue ramp; a later swap is token-only.
        info: "bg-info text-white hover:bg-info-hover active:bg-info-hover",
        // For dark and image backgrounds (Figma fill-white / fill-white-hover).
        white: "bg-white text-foreground hover:bg-muted active:bg-muted",
        // White Outline rests on --border-color-hairline #eaeaea (--border-subtle),
        // not pure white, and hovers by darkening the boundary to #bcbcbc while the
        // label stays white — it does NOT invert to a white fill (node 10562:96865).
        "white-outline":
          "border border-border-subtle bg-transparent text-white hover:border-border-hover active:border-border-hover",
        // Chromatic Ghost styles (Figma Style=Ghost on Success/Attention/Warning).
        // Transparent resting, the glyph in the on-light chromatic TEXT color — which
        // is a different ramp step from the fill of the same name — hovering to that
        // ramp's subtle tint. Figma defines these on the ICON button only; using them
        // with a label is a documented repo extension, like the `md` size.
        "success-ghost":
          "text-success-text hover:bg-success-foreground active:bg-success-foreground",
        // --error IS Figma's --text-color-attention #c21100, same role and value.
        "destructive-ghost":
          "text-error hover:bg-destructive-foreground active:bg-destructive-foreground",
        "warning-ghost":
          "text-warning-text hover:bg-warning-foreground active:bg-warning-foreground",
      },
      // One name, one height, system-wide: sm 32 / md 40 / lg 48. The off-scale
      // 36px step is retired — `default` is an alias of `lg`, so the 211 unsized
      // call sites move 36px → 48px (see task 2.1j's visual QA).
      size: {
        default:
          "h-12 px-6 text-lg has-[>:is(svg,[data-slot=icon])]:px-4 [&_:is(svg,[data-slot=icon]):not([class*='size-'])]:size-6 [&_[data-slot=icon]]:text-[24px]",
        lg: "h-12 px-6 text-lg has-[>:is(svg,[data-slot=icon])]:px-4 [&_:is(svg,[data-slot=icon]):not([class*='size-'])]:size-6 [&_[data-slot=icon]]:text-[24px]",
        // md is a documented repo extension — Figma's text Button has no 40px step,
        // so its 20px icon follows Small rather than being read from a node.
        md: "h-10 px-4 text-base has-[>:is(svg,[data-slot=icon])]:px-3 [&_:is(svg,[data-slot=icon]):not([class*='size-'])]:size-5 [&_[data-slot=icon]]:text-[20px]",
        sm: "h-8 gap-1.5 px-3 text-base has-[>:is(svg,[data-slot=icon])]:px-2.5 [&_:is(svg,[data-slot=icon]):not([class*='size-'])]:size-5 [&_[data-slot=icon]]:text-[20px]",
        // Icon-only holds 24px at EVERY step, including the 32px box — the glyph
        // does not shrink with the button (Icon Button node 1658:21776).
        icon: "size-12 [&_:is(svg,[data-slot=icon]):not([class*='size-'])]:size-6 [&_[data-slot=icon]]:text-[24px]",
        "icon-sm":
          "size-8 [&_:is(svg,[data-slot=icon]):not([class*='size-'])]:size-6 [&_[data-slot=icon]]:text-[24px]",
        "icon-md":
          "size-10 [&_:is(svg,[data-slot=icon]):not([class*='size-'])]:size-6 [&_[data-slot=icon]]:text-[24px]",
        "icon-lg":
          "size-12 [&_:is(svg,[data-slot=icon]):not([class*='size-'])]:size-6 [&_[data-slot=icon]]:text-[24px]",
      },
    },
    compoundVariants: [
      // The single opt-out from the shared disabled fill. `link` has no Figma
      // counterpart — it is a repo extension — and a hyperlink that becomes a solid
      // grey block when disabled is plainly wrong, so it greys its label only.
      // Unrelated to the ghost ruling above, which is about state legibility.
      {
        variant: "link" as const,
        class: "disabled:bg-transparent",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
